import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, ExternalLink } from "lucide-react";
import { API } from "@/lib/faro";

// Panel "Reseñas Google": reseñas del Perfil de Empresa de cada centro.
// Datos: GET /api/admin/resenas-google (se rellenan con la lectura diaria).

const MES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sept", "oct", "nov", "dic"];
const RE_P = /recuperaci[oó]n (total |parcial )?(de |del )?(los |el |la )?(puntos|carn|permiso|licencia)|recuperar (los )?puntos|renovar puntos|curso (de )?(recuperaci|sensibili|reeducaci)|sensibilizaci|reeducaci[oó]n vial|curso (parcial|total)|permiso por puntos/i;
const isPuntos = (r) => r.p === true || (r.p !== false && RE_P.test(r.t || ""));

const C = {
  good: "#3F8F5F",
  bad: "#C2412D",
  warn: "#B7791F",
  barSoft: "#C9D6E8",
  line: "#E2E6EE",
  faint: "#8A93A4",
};

function hoy() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
function toD(s) { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); }
function daysAgo(s) { return Math.round((hoy() - toD(s)) / 864e5); }
function fmt(s, aprox) {
  const d = toD(s); const n = daysAgo(s);
  if (!aprox) { if (n <= 0) return "hoy"; if (n === 1) return "ayer"; if (n < 7) return `hace ${n} días`; }
  return `${aprox ? "≈ " : ""}${d.getDate()} ${MES[d.getMonth()]} ${d.getFullYear()}`;
}
const avg = (a) => (a.length ? a.reduce((s, r) => s + r.e, 0) / a.length : 0);
const one = (n) => n.toFixed(1).replace(".", ",");
function months(n) {
  const out = []; const t = hoy();
  for (let i = n - 1; i >= 0; i--) { const d = new Date(t.getFullYear(), t.getMonth() - i, 1); out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`); }
  return out;
}
const mesLabel = (m) => MES[+m.slice(5) - 1];
const gUrl = (gid) => (gid ? `https://business.google.com/n/${gid}/reviews?authuser=aefaro%40gmail.com` : "https://business.google.com/reviews?authuser=aefaro%40gmail.com");

function Stars({ n, size = 13 }) {
  return (
    <span className="inline-flex gap-[1px]" style={{ fontSize: size }} aria-label={`${n} estrellas`}>
      {[1, 2, 3, 4, 5].map((i) => <span key={i} style={{ color: i <= n ? "#E0A030" : "#D5DAE2" }}>★</span>)}
    </span>
  );
}

function Pill({ tone = "neutral", children }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-600",
    good: "bg-emerald-50 text-emerald-700",
    bad: "bg-red-50 text-red-700",
    warn: "bg-amber-50 text-amber-700",
    info: "bg-blue-50 text-blue-700",
  };
  return <span className={`text-[11.5px] font-semibold rounded-full px-2 py-[1px] whitespace-nowrap ${tones[tone]}`}>{children}</span>;
}

function Spark({ revs }) {
  const ms = months(12);
  const data = ms.map((m) => { const a = revs.filter((r) => r.f.startsWith(m)); return { m, g: a.filter((r) => r.e >= 4).length, b: a.filter((r) => r.e <= 3).length }; });
  const max = Math.max(1, ...data.map((d) => d.g + d.b)); const W = 240, H = 46, bw = W / 12, ih = H - 6;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-[46px] block" aria-hidden="true">
        {data.map((d, i) => {
          const x = i * bw + 2, w = bw - 4; const tip = `${mesLabel(d.m)} ${d.m.slice(0, 4)}: ${d.g} buenas, ${d.b} malas`;
          if (!d.g && !d.b) return <rect key={i} x={x} y={H - 3} width={w} height={1.5} fill={C.line}><title>{tip}</title></rect>;
          const hg = (d.g / max) * ih, hb = Math.max(d.b ? 2.5 : 0, (d.b / max) * ih);
          return (
            <g key={i}>
              {d.g > 0 && <rect x={x} y={H - 2 - hg} width={w} height={Math.max(2, hg)} rx={1.5} fill={C.good}><title>{tip}</title></rect>}
              {d.b > 0 && <rect x={x} y={H - 2 - hg - hb} width={w} height={hb} rx={1.5} fill={C.bad}><title>{tip}</title></rect>}
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[11px] -mt-1" style={{ color: C.faint }}>
        <span>{mesLabel(ms[0])}</span><span>buenas / malas por mes</span><span>{mesLabel(ms[11])}</span>
      </div>
    </div>
  );
}

function CentroCard({ c, selected, onClick }) {
  const R = c.resenas || []; const m = avg(R);
  const r90 = R.filter((r) => daysAgo(r.f) <= 90); const m90 = avg(r90);
  const n7 = R.filter((r) => daysAgo(r.f) <= 7).length, n30 = R.filter((r) => daysAgo(r.f) <= 30).length;
  const pend = r90.filter((r) => !r.r).length, low = r90.filter((r) => r.e <= 3).length;
  let trend;
  if (r90.length >= 3) { const d = m90 - m; trend = <Pill tone={d > 0.05 ? "good" : d < -0.05 ? "bad" : "neutral"}>{d > 0.05 ? "▲ " : d < -0.05 ? "▼ " : ""}90 días: {one(m90)}</Pill>; }
  else trend = <Pill>90 días: {r90.length} reseña{r90.length === 1 ? "" : "s"}</Pill>;
  const dist = [5, 4, 3, 2, 1].map((s) => R.filter((r) => r.e === s).length);
  const col = { 5: C.good, 4: C.barSoft, 3: C.warn, 2: C.bad, 1: C.bad };
  return (
    <button type="button" onClick={onClick} aria-pressed={selected}
      className={`text-left bg-white rounded-2xl border p-4 flex flex-col gap-3 transition hover:border-[var(--faro-blue,#1D4E89)] ${selected ? "border-[var(--faro-blue,#1D4E89)] ring-1 ring-[var(--faro-blue,#1D4E89)]" : "border-slate-200"}`}>
      <div className="flex justify-between gap-2">
        <div><div className="font-black text-[17px]">{c.nombre}</div><div className="text-xs text-slate-500">{c.direccion}</div></div>
        <div className="text-right shrink-0"><div className="text-3xl font-black leading-none tabular-nums">{R.length ? one(m) : "–"}</div><Stars n={Math.round(m)} /><div className="mt-1">{trend}</div></div>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {[[R.length, "total"], [n7, "7 días"], [n30, "30 días"], [low || pend, low ? "alertas 90 d" : "sin resp. 90 d", low > 0]].map(([v, l, bad], i) => (
          <div key={i}><div className={`font-black text-lg tabular-nums ${bad ? "text-red-700" : ""}`}>{v}</div><div className="text-[11px] text-slate-500">{l}</div></div>
        ))}
      </div>
      <Spark revs={R} />
      <div className="flex h-1.5 rounded overflow-hidden bg-slate-100" title={dist.map((n, i) => `${5 - i}★: ${n}`).join(" · ")}>
        {dist.map((n, i) => (n ? <span key={i} style={{ width: `${(n / R.length) * 100}%`, background: col[5 - i] }} /> : null))}
      </div>
    </button>
  );
}

function MonthlyChart({ revs }) {
  const ms = months(12);
  const counts = ms.map((m) => revs.filter((r) => r.f.startsWith(m)));
  const max = Math.max(4, ...counts.map((a) => a.length));
  const step = max <= 10 ? 2 : max <= 25 ? 5 : 10; const top = Math.ceil(max / step) * step;
  const L = 36, Rr = 10, T = 14, B = 30, W = 960, H = 200, iw = W - L - Rr, ih = H - T - B, bw = iw / 12;
  const ticks = []; for (let v = 0; v <= top; v += step) ticks.push(v);
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[560px] h-auto" role="img" aria-label="Reseñas por mes">
        {ticks.map((v) => { const y = T + ih - (v / top) * ih; return <g key={v}><line x1={L} x2={W - Rr} y1={y} y2={y} stroke={C.line} /><text x={L - 8} y={y + 4} textAnchor="end" fontSize="11" fill={C.faint}>{v}</text></g>; })}
        {counts.map((a, i) => {
          const ng = a.filter((r) => r.e >= 4).length, nb = a.filter((r) => r.e <= 3).length;
          const hg = (ng / top) * ih, hb = nb ? Math.max((nb / top) * ih, 3) : 0; const x = L + i * bw + bw * 0.2, w = bw * 0.6, base = T + ih;
          const tip = `${mesLabel(ms[i])} ${ms[i].slice(0, 4)}: ${ng} buenas, ${nb} malas${a.length ? ` · nota ${one(avg(a))}` : ""}`;
          const y = base - hg - hb;
          return (
            <g key={i}>
              {ng > 0 && <rect x={x} y={base - hg} width={w} height={Math.max(hg, 2)} rx={3} fill={C.good}><title>{tip}</title></rect>}
              {nb > 0 && <rect x={x} y={base - hg - hb} width={w} height={hb} rx={3} fill={C.bad}><title>{tip}</title></rect>}
              {a.length > 0 && <text x={x + w / 2} y={y - 5} textAnchor="middle" fontSize="11" fill="#5A6477">{ng}{nb ? <tspan fill={C.bad} fontWeight="600">{` · ${nb}`}</tspan> : null}</text>}
              <text x={x + w / 2} y={H - 10} textAnchor="middle" fontSize="11" fill={C.faint}>{mesLabel(ms[i])}{ms[i].slice(5) === "01" || i === 0 ? ` ${ms[i].slice(2, 4)}` : ""}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ReviewItem({ r, gid, showWhere, canUnmark, onUnmark }) {
  const [open, setOpen] = useState(false);
  const long = (r.t || "").length > 320;
  const isNew = r.vista && daysAgo(r.vista) <= 1;
  const sev = r.e <= 2 ? C.bad : r.e === 3 ? C.warn : C.line;
  return (
    <div className="relative grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 px-2 py-3 border-t border-slate-100 first:border-t-0 rounded-lg hover:bg-slate-50">
      <span className="w-1 rounded row-span-3" style={{ background: sev }} />
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px]">
        <Stars n={r.e} />
        <a href={gUrl(gid)} target="_blank" rel="noopener noreferrer" title="Abrir en Google para responder"
          className="font-semibold text-slate-900 after:content-[''] after:absolute after:inset-0 after:rounded-lg">{r.a}</a>
        {showWhere && <span className="text-slate-500">{r.cn}</span>}
        <span style={{ color: C.faint }}>{fmt(r.f, r.aprox)}</span>
        {isNew && <Pill tone="info">Nueva</Pill>}
        {isPuntos(r) && <Pill tone="info">Curso de puntos</Pill>}
        {r.r ? <Pill tone="good">Respondida</Pill> : <Pill tone="warn">Sin responder</Pill>}
        {canUnmark && (
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onUnmark(r); }}
            className="relative z-10 text-xs font-semibold text-red-700 bg-red-50 rounded-full px-2.5 py-0.5 hover:bg-red-100">✕ No es de puntos</button>
        )}
        <span className="ml-auto text-xs font-semibold text-[var(--faro-blue,#1D4E89)] whitespace-nowrap inline-flex items-center gap-1">
          {r.r ? "Ver en Google" : "Responder en Google"} <ExternalLink size={12} />
        </span>
      </div>
      {r.t ? (
        <>
          <p className={`m-0 text-sm break-words ${long && !open ? "line-clamp-4" : ""}`}>{r.t}</p>
          {long && (
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
              className="relative z-10 justify-self-start col-start-2 text-[13px] font-semibold text-[var(--faro-blue,#1D4E89)]">{open ? "Ver menos" : "Ver todo"}</button>
          )}
        </>
      ) : <p className="m-0 text-sm italic" style={{ color: C.faint }}>Solo valoración, sin texto</p>}
    </div>
  );
}

export default function GoogleReviewsPanel({ authHeader }) {
  const [centros, setCentros] = useState({});
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [chart, setChart] = useState("all");
  const [onlyPuntos, setOnlyPuntos] = useState(false);
  const [onlyPending, setOnlyPending] = useState(false);
  const [feedN, setFeedN] = useState(25);
  const [alertsAll, setAlertsAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/resenas-google`, { headers: authHeader });
      setCentros(data.centros || {}); setEstado(data.estado || null);
    } catch (e) {
      toast.error("No se pudieron cargar las reseñas");
    } finally { setLoading(false); }
  }, [authHeader]);
  useEffect(() => { load(); }, [load]);

  const S = useMemo(() => Object.entries(centros).map(([id, c]) => ({ id, ...c })).filter((c) => c.estado === "activo").sort((a, b) => (a.orden || 99) - (b.orden || 99)), [centros]);
  const all = useCallback((pred) => {
    const out = [];
    S.forEach((c) => { if (!pred || pred(c)) (c.resenas || []).forEach((r) => out.push({ ...r, cid: c.id, cn: c.nombre, gid: c.gid })); });
    return out.sort((a, b) => b.f.localeCompare(a.f));
  }, [S]);

  const R = useMemo(() => all(), [all]);
  const inD = (n) => R.filter((r) => daysAgo(r.f) <= n);
  const rec90 = inD(90);

  const setPuntos = async (r, val) => {
    try {
      await axios.patch(`${API}/admin/resenas-google/${encodeURIComponent(r.cid)}/${encodeURIComponent(r.id)}/puntos`, { p: val }, { headers: authHeader });
      setCentros((prev) => {
        const c = prev[r.cid]; if (!c) return prev;
        return { ...prev, [r.cid]: { ...c, resenas: c.resenas.map((x) => (x.id === r.id ? { ...x, p: val } : x)) } };
      });
      if (!val) toast("Quitada de la lista de cursos de puntos", { action: { label: "Deshacer", onClick: () => setPuntos(r, true) } });
    } catch (e) { toast.error("No se pudo guardar el cambio"); }
  };

  const fil = filter === "all" ? null : (c) => c.id === filter;
  const alertsAllList = all(fil).filter((r) => r.e <= 3);
  const alerts12 = alertsAllList.filter((r) => daysAgo(r.f) <= 365);
  let feed = all(fil); if (onlyPending) feed = feed.filter((r) => !r.r); if (onlyPuntos) feed = feed.filter(isPuntos);
  const chartRevs = all(chart === "all" ? null : (c) => c.id === chart);
  const chartName = chart === "all" ? "todos los centros" : (S.find((c) => c.id === chart) || {}).nombre;
  const chartG = chartRevs.filter((r) => r.f >= months(12)[0] && r.e >= 4).length, chartB = chartRevs.filter((r) => r.f >= months(12)[0] && r.e <= 3).length;

  let status = "Sin lecturas todavía"; let stale = true;
  if (estado?.error) status = `Última lectura falló: ${estado.error}`;
  else if (estado?.ultimaLectura) {
    const d = new Date(estado.ultimaLectura); stale = (Date.now() - d) / 36e5 > 30;
    status = `Última lectura: ${d.toLocaleDateString("es-ES", { day: "numeric", month: "short" })} ${d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}${estado.nuevasUltimaLectura != null ? ` · ${estado.nuevasUltimaLectura} nuevas` : ""}`;
  }

  if (loading && !S.length) return <div className="text-slate-500 py-10 text-center">Cargando reseñas…</div>;
  if (!S.length) return <div className="text-slate-500 py-10 text-center">Todavía no hay reseñas cargadas.</div>;

  const chip = (active) => `text-[13px] rounded-full px-3 py-1 border ${active ? "bg-[var(--faro-blue,#1D4E89)] border-[var(--faro-blue,#1D4E89)] text-white" : "bg-white border-slate-200 text-slate-800"}`;
  const kpis = [
    ["Nota media", <>{one(avg(R))}<small className="text-sm text-slate-500 font-medium ml-1">/5</small></>, `${R.length} reseñas en ${S.length} centros`],
    ["Hoy y ayer", inD(1).length, "reseñas nuevas"],
    ["Últimos 7 días", inD(7).length, "reseñas nuevas"],
    ["Últimos 30 días", inD(30).length, `nota ${inD(30).length ? one(avg(inD(30))) : "–"}`],
    ["Sin responder", rec90.filter((r) => !r.r).length, `de ${rec90.length} en 90 días`],
    ["Alertas 1-3 ★", rec90.filter((r) => r.e <= 3).length, "en los últimos 90 días", rec90.some((r) => r.e <= 3)],
  ];

  return (
    <div className="flex flex-col gap-6" data-testid="google-reviews-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black">Reseñas de Google</h2>
          <p className="text-sm text-slate-500">Reseñas del Perfil de Empresa de cada centro · se actualizan cada día a las 8:45</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 text-[13px] text-slate-600 bg-white border border-slate-200 rounded-full px-3 py-1">
            <span className="w-2 h-2 rounded-full" style={{ background: stale || estado?.error ? C.warn : C.good }} />{status}
          </span>
          <button type="button" onClick={load} className="btn-pill btn-ghost text-sm" title="Recargar"><RefreshCw size={15} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {kpis.map(([l, v, h, alert], i) => (
          <div key={i} className="p-4 flex flex-col gap-1 border-slate-100 border-l border-t -ml-px -mt-px">
            <span className="text-[11.5px] uppercase tracking-wider font-semibold text-slate-500">{l}</span>
            <span className={`text-3xl font-black leading-tight tabular-nums ${alert ? "text-red-700" : ""}`}>{v}</span>
            <span className="text-xs text-slate-500">{h}</span>
          </div>
        ))}
      </div>

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap justify-between items-center gap-2">
          <h3 className="text-lg font-black">Centros</h3>
          <span className="text-[13px] text-slate-500">Pulsa un centro para filtrar las reseñas</span>
        </div>
        <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(290px,1fr))]">
          {S.map((c) => <CentroCard key={c.id} c={c} selected={filter === c.id} onClick={() => { setFilter(filter === c.id ? "all" : c.id); setFeedN(25); }} />)}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col gap-3">
        <div className="flex flex-wrap justify-between items-center gap-2">
          <h3 className="text-lg font-black">Reseñas por mes</h3>
          <div className="flex gap-4 text-[13px] text-slate-500">
            <span className="inline-flex items-center gap-1.5"><i className="w-3 h-3 rounded-sm inline-block" style={{ background: C.good }} />Buenas (4-5 ★)</span>
            <span className="inline-flex items-center gap-1.5"><i className="w-3 h-3 rounded-sm inline-block" style={{ background: C.bad }} />Malas (1-3 ★)</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" className={chip(chart === "all")} onClick={() => setChart("all")}>Todos los centros</button>
          {S.map((c) => <button type="button" key={c.id} className={chip(chart === c.id)} onClick={() => setChart(c.id)}>{c.nombre}</button>)}
        </div>
        <div className="text-[13px] text-slate-500">Últimos 12 meses · {chartName} · {chartG} buenas y {chartB} malas</div>
        <MonthlyChart revs={chartRevs} />
      </section>

      <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] items-start">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col gap-3 min-w-0">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <h3 className="text-lg font-black">Alertas 1-3 ★</h3>
            <Pill tone="warn">{alerts12.filter((r) => !r.r).length} sin responder en 12 meses</Pill>
          </div>
          <div className="text-xs text-slate-500">Pulsa una reseña para abrirla en Google y responderla (con la cuenta aefaro@ abierta).</div>
          <div className="flex flex-col">
            {(alertsAll ? alertsAllList : alerts12).length
              ? (alertsAll ? alertsAllList : alerts12).map((r) => <ReviewItem key={r.cid + r.id} r={r} gid={r.gid} showWhere />)
              : <div className="text-sm text-slate-500 py-2">Ninguna reseña de 1 a 3 estrellas en los últimos 12 meses.</div>}
          </div>
          {!alertsAll && alertsAllList.length > alerts12.length && (
            <button type="button" className="text-sm text-[var(--faro-blue,#1D4E89)] border border-slate-200 rounded-lg py-2" onClick={() => setAlertsAll(true)}>
              Ver alertas anteriores ({alertsAllList.length - alerts12.length})
            </button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-col gap-3 min-w-0">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <h3 className="text-lg font-black">Últimas reseñas</h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-slate-600">
              <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={onlyPuntos} onChange={(e) => { setOnlyPuntos(e.target.checked); setFeedN(25); }} /> Solo cursos de puntos / sensibilización</label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={onlyPending} onChange={(e) => { setOnlyPending(e.target.checked); setFeedN(25); }} /> Solo sin responder</label>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className={chip(filter === "all")} onClick={() => { setFilter("all"); setFeedN(25); }}>Todos los centros</button>
            {S.map((c) => <button type="button" key={c.id} className={chip(filter === c.id)} onClick={() => { setFilter(c.id); setFeedN(25); }}>{c.nombre}</button>)}
          </div>
          <div className="text-xs text-slate-500">
            Pulsa una reseña para abrirla en Google y responderla (con la cuenta aefaro@ abierta).
            {onlyPuntos && " Si alguna no es de cursos de puntos, pulsa «✕ No es de puntos» para quitarla de esta lista."}
          </div>
          <div className="flex flex-col">
            {feed.length
              ? feed.slice(0, feedN).map((r) => (
                <ReviewItem key={r.cid + r.id} r={r} gid={r.gid} showWhere={filter === "all"}
                  canUnmark={onlyPuntos && isPuntos(r)} onUnmark={(x) => setPuntos(x, false)} />
              ))
              : <div className="text-sm text-slate-500 py-2">No hay reseñas con este filtro.</div>}
          </div>
          {feed.length > feedN && (
            <button type="button" className="text-sm text-[var(--faro-blue,#1D4E89)] border border-slate-200 rounded-lg py-2" onClick={() => setFeedN((n) => n + 50)}>
              Ver más ({feed.length - feedN} restantes)
            </button>
          )}
        </div>
      </section>

      <p className="text-xs text-slate-400">
        Fuente: Perfil de Empresa de Google. La nota media se calcula con las reseñas leídas; Google puede redondearla distinto.
        Las fechas con ≈ son aproximadas (±1 semana): Google solo muestra «hace N semanas» para el último año.
      </p>
    </div>
  );
}
